import React from 'react';
import s from './s.module.less';
import Cheer from './cheers.png';
import dayjs from 'dayjs';
import { Button } from 'antd';
import DateSelect from '../DateSelect';
import WebinarBooking from 'components/WebinarBooking';
import GrowthGoalModal from '../GrowthGoalModal';
import { CALENDLY_LINK } from 'constants/common';
import { updateLaunchTime } from 'api/ppaOnboarding';
import { getKlarityProviderHost } from 'utils/common';

type Props = {
    onGoToAdvancedSetup: () => void;
    enableGrowthGoal?: boolean;
};

export default function SetGolive({ onGoToAdvancedSetup, enableGrowthGoal = true }: Props) {
    const [goLiveTime, setGoLiveTime] = React.useState<number>(dayjs().add(48, 'hours').valueOf());
    const [dateSelectOpen, setDateSelectOpen] = React.useState<boolean>(false);
    const [showWebinar, setShowWebinar] = React.useState(false);
    const [showGrowthGoalModal, setShowGrowthGoalModal] = React.useState(false);
    const [confirmLoading, setConfirmLoading] = React.useState(false);
    const handleDateConfirm = async (date: dayjs.Dayjs) => {
        setConfirmLoading(true);
        const res = await updateLaunchTime(date.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'));
        setConfirmLoading(false);
        if (!res.error) {
            setGoLiveTime(date.valueOf());
            setDateSelectOpen(false);
        }
    };

    const handleOpenDateSelect = () => {
        setDateSelectOpen(true);
    };

    const handleDateSelectCancel = () => {
        setDateSelectOpen(false);
    };

    return (
        <div className={s.cWrap}>
            <div className={s.icon}>
                <img src={Cheer} alt="cheer" />
            </div>
            <h2>You&apos;re all set to go live!</h2>
            <div className={s.subTitle}>
                Your profile is expected to be approved within 24 hours. We&apos;ll email you once it&apos;s ready, so
                please check your inbox. After going live, you&apos;ll be able to welcome patients and start booking
                right away.
            </div>

            <Button
                onClick={() => (enableGrowthGoal ? setShowGrowthGoalModal(true) : onGoToAdvancedSetup())}
                type="primary"
                className={s.goBtn}
            >
                Go to advanced setup
            </Button>
            <div className={s.supportTips}>
                <p>
                    Advanced settings and future changes can only be made on a desktop or laptop. Please use a computer
                    to continue.
                </p>
                <div className={s.supportLink}>
                    <a href={getKlarityProviderHost()}>{getKlarityProviderHost()}</a>
                </div>
            </div>

            <div className={s.itemWrap}>
                <div className={s.item}>
                    <div className={s.left}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                            <path
                                d="M16.3333 4.1665H4.66667C3.74619 4.1665 3 4.9127 3 5.83317V16.6665C3 17.587 3.74619 18.3332 4.66667 18.3332H16.3333C17.2538 18.3332 18 17.587 18 16.6665V5.83317C18 4.9127 17.2538 4.1665 16.3333 4.1665Z"
                                stroke="#0E62F4"
                                strokeWidth="1.875"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6.33333 1.66663V4.16663M14.6667 1.66663V4.16663M3 8.33329H18"
                                stroke="#0E62F4"
                                strokeWidth="1.875"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className={s.right}>
                        <div className={s.title}>Expected go-live date</div>
                        <div className={s.time}>{dayjs(goLiveTime).format('dddd, MMM D, YYYY')}</div>
                        <div className={s.need} onClick={handleOpenDateSelect}>
                            Need more time?
                        </div>
                    </div>
                </div>
            </div>
            <div className={s.itemWrap}>
                <div className={s.item}>
                    <div className={s.left}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                            <path
                                d="M3 13.625V7.37504C3 6.93301 3.17559 6.50909 3.48816 6.19653C3.80072 5.88397 4.22464 5.70837 4.66667 5.70837H11.75C12.192 5.70837 12.616 5.88397 12.9285 6.19653C13.2411 6.50909 13.4167 6.93301 13.4167 7.37504V13.625C13.4167 14.0671 13.2411 14.491 12.9285 14.8036C12.616 15.1161 12.192 15.2917 11.75 15.2917H4.66667C4.22464 15.2917 3.80072 15.1161 3.48816 14.8036C3.17559 14.491 3 14.0671 3 13.625ZM17.3067 6.32587L13.9733 9.29504C13.9294 9.33405 13.8942 9.38191 13.8701 9.43548C13.846 9.48905 13.8334 9.54712 13.8333 9.60587V11.0875C13.8334 11.1463 13.846 11.2044 13.8701 11.2579C13.8942 11.3115 13.9294 11.3594 13.9733 11.3984L17.3067 14.3675C17.3667 14.4209 17.4409 14.4557 17.5203 14.4679C17.5997 14.48 17.6809 14.469 17.7542 14.436C17.8274 14.4031 17.8896 14.3497 17.9332 14.2822C17.9768 14.2148 18 14.1362 18 14.0559V6.63754C18 6.55722 17.9768 6.47861 17.9332 6.41117C17.8896 6.34372 17.8274 6.29032 17.7542 6.25739C17.6809 6.22446 17.5997 6.21341 17.5203 6.22556C17.4409 6.23771 17.3667 6.27254 17.3067 6.32587Z"
                                stroke="#0E62F4"
                                strokeWidth="1.875"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className={s.right}>
                        <div className={s.title}>Go-live webinar (optional but encouraged)</div>
                        <div className={s.des}>Meet your success manager before going live</div>
                    </div>
                </div>
                <div className={s.note}>
                    <div>
                        Join our Klarity Onboarding Webinar to review structure, no-show fees, and best practices. You
                        may have already attended this same webinar earlier—this isn’t a new session, but you’re welcome
                        to join again as a refresher.
                    </div>
                    <Button
                        onClick={() => {
                            setShowWebinar(true);
                        }}
                        className={s.btn}
                        block
                    >
                        Schedule webinar
                    </Button>
                </div>
            </div>

            {/* DateSelect Modal */}
            <DateSelect
                open={dateSelectOpen}
                onCancel={handleDateSelectCancel}
                onConfirm={handleDateConfirm}
                confirmLoading={confirmLoading}
                disabledDate={(date: dayjs.Dayjs) => {
                    //48 hours after
                    return date.isBefore(dayjs().add(48, 'hours'));
                }}
            />
            <WebinarBooking
                title="Join support webinar"
                show={showWebinar}
                link={CALENDLY_LINK.KLARITY_ONBOARDING_MEET_SUCCESS_MANAGER}
                onClose={(show) => {
                    setShowWebinar(show);
                }}
            />
            {enableGrowthGoal && (
                <GrowthGoalModal
                    open={showGrowthGoalModal}
                    onCancel={() => setShowGrowthGoalModal(false)}
                    onConfirm={() => {
                        setShowGrowthGoalModal(false);
                        onGoToAdvancedSetup();
                    }}
                />
            )}
        </div>
    );
}
